/**
 * Parses CSV text into an array of rows
 * @param {string} text - The CSV text to parse
 * @param {number} fileSize - The size of the file in bytes
 * @param {number} maxFileSize - Maximum file size to process fully
 * @param {number} maxRows - Maximum number of rows to parse
 * @param {number} chunkSize - Size of chunk to process for large files
 * @returns {Array} - Array of rows, each row is an array of cell values
 */
export function parseCSV(text, fileSize, maxFileSize, maxRows, chunkSize) {
  const rows = [];
  let currentRow = '';
  let inQuotes = false;
  let i = 0;

  while (rows.length < maxRows && i < text.length) {
    const char = text[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === '\n' && !inQuotes) {
      if (currentRow.trim()) {
        rows.push(parseRow(currentRow));
        currentRow = '';
        if (fileSize > maxFileSize && rows.length >= maxRows) {
          break;
        }
      }
    } else {
      currentRow += char;
    }
    i++;
  }

  if (currentRow.trim() && rows.length < maxRows) {
    rows.push(parseRow(currentRow));
  }

  return rows;
}

/**
 * Parses a single CSV row into an array of cell values
 * @param {string} row - The CSV row to parse
 * @returns {Array} - Array of cell values
 */
export function parseRow(row) {
  const cells = [];
  let currentCell = '';
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      cells.push(currentCell.trim());
      currentCell = '';
    } else {
      currentCell += char;
    }
  }

  cells.push(currentCell.trim());
  return cells;
}

/**
 * Processes a file by reading it as text and parsing it as CSV
 * @param {File} file - The file to process
 * @param {number} maxFileSize - Maximum file size to process fully
 * @param {number} chunkSize - Size of chunk to process for large files
 * @param {Function} onSuccess - Callback for successful processing
 * @param {Function} onError - Callback for error handling
 */
export function processCSVFile(file, maxFileSize, chunkSize, onSuccess, onError) {
  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const text = e.target.result;
      onSuccess(text, file.size);
    } catch (error) {
      onError(error);
    }
  };
  reader.onerror = () => onError(new Error('Error reading file'));

  const readSize = file.size > maxFileSize ? chunkSize : file.size;
  const blob = file.slice(0, readSize);
  reader.readAsText(blob);
}
